import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import * as v from 'valibot';

const bugReportSchema = v.object({
  title: v.pipe(v.string(), v.nonEmpty('Title is required')),
  description: v.optional(v.string(), ''),
  priority: v.picklist(['lowest', 'low', 'medium', 'high', 'highest']),
});

type BugReportFormData = v.InferInput<typeof bugReportSchema>;

interface BugReportModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BugReportFormData) => Promise<void>;
  screenshotPreview: string | null;
  isSubmitting: boolean;
}

export function BugReportModal({
  open,
  onClose,
  onSubmit,
  screenshotPreview,
  isSubmitting,
}: BugReportModalProps) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<BugReportFormData>({
    resolver: valibotResolver(bugReportSchema),
    defaultValues: { title: '', description: '', priority: 'medium' },
  });

  if (!open) return null;

  const handleFormSubmit = async (data: BugReportFormData) => {
    await onSubmit(data);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Report a Bug</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="bug-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              id="bug-title"
              type="text"
              {...register('title')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Brief description of the issue"
              autoFocus
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="bug-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="bug-description"
              {...register('description')}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Steps to reproduce, expected vs actual behavior..."
            />
          </div>

          <div>
            <label htmlFor="bug-priority" className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              id="bug-priority"
              {...register('priority')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lowest">Lowest</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="highest">Highest</option>
            </select>
          </div>

          {screenshotPreview && (
            <div>
              <p className="block text-sm font-medium text-gray-700 mb-1">Screenshot</p>
              <img
                src={`data:image/jpeg;base64,${screenshotPreview}`}
                alt="Screenshot preview"
                className="w-full rounded-lg border border-gray-200 max-h-40 object-contain bg-gray-50"
              />
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
